import { Matrix } from 'ml-matrix';
import { getShortestPath } from './getShortestPath';
/**
 * Implementation of Jonker-Volgenant shortest augmenting path algorithm based
 * on the publication [On implementing 2D rectangular assignment algorithms](https://doi.org/10.1109/TAES.2016.140952)
 */
export function linearSumAssignment(
/**
 * Cost matrix
 */
input, options = {}) {
    const { maximaze = true } = options;
    let matrix = Matrix.checkMatrix(input);
    let didFlip = false;
    if (matrix.columns > matrix.rows) {
        didFlip = true;
        matrix = matrix.transpose();
    }
    let nbRows = matrix.rows;
    let nbColumns = matrix.columns;
    let matrixDelta = maximaze ? matrix.max() : matrix.min();
    matrix = matrix.subtract(matrixDelta);
    if (maximaze)
        matrix = matrix.mul(-1);
    let rowAssignments = new Float64Array(nbRows).fill(-1);
    let columnAssignments = new Float64Array(nbColumns).fill(-1);
    let dualVariableForColumns = new Float64Array(nbColumns);
    let dualVariableForRows = new Float64Array(nbRows);
    for (let currUnAssCol = 0; currUnAssCol < nbColumns; currUnAssCol++) {
        let currentAugmenting = getShortestPath({
            matrix,
            currUnAssCol,
            dualVariableForColumns,
            dualVariableForRows,
            rowAssignments,
            columnAssignments,
        });
        let { sink, pred } = currentAugmenting;
        if (sink === -1) {
            return {
                rowAssignments,
                columnAssignments,
                gain: -1,
                dualVariableForColumns,
                dualVariableForRows,
            };
        }
        dualVariableForColumns = currentAugmenting.dualVariableForColumns;
        dualVariableForRows = currentAugmenting.dualVariableForRows;
        let j = sink;
        for (let i = pred[j]; true; i = pred[j]) {
            rowAssignments[j] = i;
            let h = columnAssignments[i];
            columnAssignments[i] = j;
            j = h;
            if (i === currUnAssCol)
                break;
        }
    }
    let gain = 0;
    for (let curCol = 0; curCol < nbColumns; curCol++) {
        gain += matrix.get(columnAssignments[curCol], curCol);
    }
    gain = (maximaze ? -1 : 1) * gain + matrixDelta * nbColumns;
    if (didFlip) {
        [columnAssignments, rowAssignments] = [rowAssignments, columnAssignments];
        [dualVariableForColumns, dualVariableForRows] = [
            dualVariableForRows,
            dualVariableForColumns,
        ];
    }
    return {
        rowAssignments,
        columnAssignments,
        gain,
        dualVariableForColumns,
        dualVariableForRows,
    };
}
//# sourceMappingURL=data:application/json;base64,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